import React, { useEffect, useState, useCallback } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../store';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import Input from '../shared/TailwindComponents/Input';
import Textarea from '../shared/TailwindComponents/Textarea';
import Dropdown, { DropdownOption } from '../shared/TailwindComponents/Dropdown';
import Button from '../shared/TailwindComponents/Button';
import { fetchAllTeams } from '../../store/actions/organization/teamActions';
import { fetchOrgPeople } from '../../store/actions/OnboardingActions';
import { fetchMatters } from '../../store/actions/Matters/MatterActions';
import FileUploader from '../shared/TailwindComponents/FileUploader';
import DateTimePicker from '../shared/TailwindComponents/DatePicker';
import GlassCard from '../shared/brand/GlassCard';
import { useHistory } from 'react-router-dom';
import { PiCheckSquareOffsetLight } from 'react-icons/pi';
import { useTranslation } from 'react-i18next';
import apis from '../../utils/apis';

// Import validation system
import { createTaskSchema, sanitizeFormData, validateForm } from '../../utils/validation';
import { useLicenseGate } from '../../hooks/useLicenseGate';
import { readErrorMessage } from '../../utils/errorMessage';

interface AddTaskProps {
  onCancel?: () => void;
  onAdd?: () => void;
  teamId?: string;
}

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  user_id: number;
}

interface Collaborator {
  user_id: string | number;
  access: 'edit' | 'view';
}

interface TaskFormValues {
  TaskTitle: string;
  TaskDescription: string;
  TaskDeadline: string;
  MatterUUID: string;
  AssigneeType: string;
  AssigneeID: string;
  files: File[];
  status: string;
  priority: string;
}

const AddTask: React.FC<AddTaskProps> = ({ onCancel, onAdd, teamId }) => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const { checkSectionGate } = useLicenseGate();

  const [matterOptions, setMatterOptions] = useState<DropdownOption[]>([]);
  const [teamOptions, setTeamOptions] = useState<DropdownOption[]>([]);
  const [scheduleDate, setScheduleDate] = useState<Date | null>(null);
  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const [isCreatingTask, setIsCreatingTask] = useState(false);

  const teams = useSelector((state: RootState) => state.teams.teams || []);
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const collaboratorsUsers = useSelector((state: RootState) => state.orgPeople?.people || []);


  const loadInitialData = useCallback(async () => {
    const promises = [];

    promises.push(
      dispatch(fetchMatters({ page: 1, page_size: 100 }) as any)
        .then((resp: any) => {
          if (resp && resp.matters) {
            const formattedMatters = resp.matters.map((matter: any) => ({
              value: matter.uuid,
              label: matter.title,
            }));
            setMatterOptions(formattedMatters);
          }
        })
    );

    promises.push(dispatch(fetchOrgPeople()));

    promises.push(
      dispatch(fetchAllTeams())
        .then(resp => {
          if (resp && resp.teams) {
            const formattedTeams = resp.teams.map((team: any) => ({
              value: team.LegalTeamID,
              label: team.TeamName,
            }));
            setTeamOptions(formattedTeams);
          }
        })
    );

    await Promise.all(promises);
  }, [dispatch]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const validationSchema = Yup.object().shape({
    TaskTitle: Yup.string().required(t('Title is required')),
    TaskDescription: Yup.string().required(t('Description is required')),
    TaskDeadline: Yup.date().required(t('Deadline is required')),
    AssigneeType: Yup.string(),
    AssigneeID: Yup.string().when('AssigneeType', {
      is: (AssigneeType: any) => AssigneeType === 'people' || AssigneeType === 'team',
      then: (schema) => schema.required(t('Please select a responsible party')),
    }),
  });

  const formik = useFormik<TaskFormValues>({
    initialValues: {
      TaskTitle: '',
      TaskDescription: '',
      TaskDeadline: '',
      MatterUUID: '',
      AssigneeType: teamId ? "team" : '',
      AssigneeID: teamId || '',
      files: [] as File[],
      status: 'todo',
      priority: 'medium',
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      if (!checkSectionGate('tasks')) return;
      try {
        setIsCreatingTask(true);

        // Validate and sanitize form data
        const taskSchema = createTaskSchema();
        const validationResult = validateForm(taskSchema, {
          title: values.TaskTitle,
          description: values.TaskDescription,
          dueDate: values.TaskDeadline,
          status: values.status,
          priority: values.priority,
        }, false);

        if (!validationResult.isValid) {
          const errorMessages = Object.values(validationResult.errors)
            .flat()
            .join('; ');
          throw new Error(`Validation failed: ${errorMessages}`);
        }

        // Sanitize all form data
        const sanitizedValues = sanitizeFormData({
          title: values.TaskTitle,
          description: values.TaskDescription,
          due_date: values.TaskDeadline,
          status: values.status,
          priority: values.priority,
          matterUUID: values.MatterUUID,
          assigneeType: values.AssigneeType,
          assigneeId: values.AssigneeID,
        });

        // Prepare FormData for single consolidated request
        const formData = new FormData();

        // Add task fields with sanitized values
        formData.append('title', sanitizedValues.title || '');
        formData.append('description', sanitizedValues.description || '');
        formData.append('due_date', sanitizedValues.due_date || '');
        formData.append('status', sanitizedValues.status || '');
        formData.append('priority', sanitizedValues.priority || '');

        if (values.MatterUUID) {
          formData.append('matter_uuid', values.MatterUUID);
        }

        if (sanitizedValues.assigneeType === 'team' && sanitizedValues.assigneeId) {
          formData.append('TeamID', String(sanitizedValues.assigneeId));
        }

        if (sanitizedValues.assigneeType === 'people' && sanitizedValues.assigneeId) {
          formData.append('assigned_to_user_id', String(sanitizedValues.assigneeId));
        }

        if (collaborators.length > 0) {
          formData.append('is_shared', 'true');
          // Add collaborators with access levels
          const validCollaborators = collaborators
            .map(c => ({
              user_id: parseInt(String(c.user_id), 10),
              access: c.access
            }))
            .filter(c => !Number.isNaN(c.user_id));
          formData.append('collaborators', JSON.stringify(validCollaborators));
        }

        // Add files if any were selected - sanitize filenames
        if (values.files && values.files.length > 0) {
          for (const file of values.files) {
            formData.append('files', file);
          }
        }

        // Make single consolidated API call
        const response = await apis.post('/tasks/create_task', formData);

        if (!response.data || !response.data.uuid) {
          throw new Error('Task UUID not returned from API');
        }

        const taskUuid = response.data.uuid;

        dispatch(
          showFeedbackModal({
            modalType: 'success',
            message: t('Task created successfully'),
            showModal: true,
            duration: 2000,
          })
        );

        if (onAdd) onAdd();
        resetForm();

        // Redirect to task details page
        setTimeout(() => {
          history.push(`/tasks/${taskUuid}`);
        }, 500);
      } catch (error: any) {
        const errorMessage = error?.response?.data?.validation_errors
          ? Object.values(error.response.data.validation_errors).join(', ')
          : readErrorMessage(error, t('Could not add task. Please contact support.'));

        dispatch(
          showFeedbackModal({
            modalType: 'error',
            duration: 3000,
            message: errorMessage,
            showModal: true,
          })
        );
      } finally {
        setIsCreatingTask(false);
      }
    },
  });

  const handleCancel = () => {
    if (onCancel) onCancel();
  };

  const titleError =
    formik.touched.TaskTitle && formik.errors.TaskTitle ? String(formik.errors.TaskTitle) : undefined;
  const descriptionError =
    formik.touched.TaskDescription && formik.errors.TaskDescription
      ? String(formik.errors.TaskDescription)
      : undefined;
  const deadlineError =
    formik.touched.TaskDeadline && formik.errors.TaskDeadline
      ? t(String(formik.errors.TaskDeadline))
      : undefined;

  return (
    <div className="flex flex-col w-full max-w-3xl mx-auto px-6 pb-6">
      <div className="sticky top-0 z-10 -mx-6 px-6 pt-6 pb-4 glass-bar border-b border-hairline flex justify-between items-center">
        <div className="flex gap-2 items-center">
          <PiCheckSquareOffsetLight size={24} className="text-fg-muted" />
          <div className="block space-y-1">
            <h2 className="text-lg text-fg-strong">
              {t('Create new task')}
            </h2>
            <p className="text-xs text-fg-muted">
              {t('Fill out this form to add your task.')}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="neutral" size="small" onClick={handleCancel}>
            {t('Cancel')}
          </Button>
        </div>
      </div>

      <form id="add-task-form" className="space-y-6 mt-6" onSubmit={formik.handleSubmit}>
        <GlassCard title={t('Task details')} subtitle={t('Enter the information for your task')}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="col-span-full">
              <Input
                id="TaskTitle"
                label={t('Task title')}
                type="text"
                name="TaskTitle"
                value={formik.values.TaskTitle}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={titleError}
                required
              />
            </div>

            <div className="col-span-full">
              <Textarea
                id="TaskDescription"
                name="TaskDescription"
                label={t('Task description')}
                value={formik.values.TaskDescription}
                onChange={formik.handleChange}
                rows={4}
                required
                error={descriptionError}
              />
            </div>

            <Dropdown
              id="priority"
              label={t('Priority')}
              options={[
                { value: 'low', label: t('Low') },
                { value: 'medium', label: t('Medium') },
                { value: 'high', label: t('High') },
                { value: 'critical', label: t('Critical') },
              ]}
              onSelect={(option) => formik.setFieldValue('priority', option.value)}
              value={formik.values.priority}
              fullWidth
            />

            <Dropdown
              id="status"
              label={t('Status')}
              options={[
                { value: 'todo', label: t('To do') },
                { value: 'in_progress', label: t('In progress') },
                { value: 'on_hold', label: t('On hold') },
                { value: 'completed', label: t('Completed') },
              ]}
              onSelect={(option) => formik.setFieldValue('status', option.value)}
              value={formik.values.status}
              fullWidth
            />

            <DateTimePicker
              id="TaskDeadline"
              label={t('Deadline')}
              selected={scheduleDate}
              onChange={(date) => {
                setScheduleDate(date);
                formik.setFieldValue('TaskDeadline', date ? date.toISOString() : '');
              }}
              placeholder={t('Select a date and time')}
              dateFormat="MMMM d, yyyy h:mm aa"
              required
              error={deadlineError}
              helperText={deadlineError}
              fullWidth
            />

            <Dropdown
              id="MatterUUID"
              label={t('Matter')}
              options={matterOptions}
              onSelect={(option) => formik.setFieldValue('MatterUUID', option.value)}
              value={formik.values.MatterUUID}
              fullWidth
            />
          </div>
        </GlassCard>

        <GlassCard
          title={t('Attachments')}
          subtitle={t('Import, upload, or drag and drop files to attach to this task (optional).')}
        >
          <FileUploader
            files={formik.values.files}
            onFilesChange={(newFiles: any) => formik.setFieldValue('files', newFiles)}
          />
        </GlassCard>

        <div className="glass-bar border-t border-hairline -mx-6 px-6 py-4 flex justify-end gap-2">
          <Button
            variant="primary"
            size="small"
            type="submit"
            form="add-task-form"
            disabled={isCreatingTask}
            isLoading={isCreatingTask}
          >
            {isCreatingTask ? t('Creating task...') : t('Create task')}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AddTask;
